/**
 * Score popups — DOM floating texts in the 9:16 viewport (Stage 2).
 *
 * Pooled: a fixed number of `<div>` elements are pre-allocated and recycled.
 * On a kick/nearMiss event, a popup spawns at a screen-space position
 * (projected from the world contact point, or a default near-centre for
 * near-miss), rises ~48px, and fades over ~700 ms.
 *
 * Zero per-frame allocation: `update` mutates `transform` and `opacity` in
 * place on the pooled elements.
 */

import { JUICE, clamp01 } from "./juice";

const PP = JUICE.popups;

interface PopupSlot {
  el: HTMLDivElement;
  active: boolean;
  elapsed: number;
  startX: number; // px within viewport
  startY: number; // px within viewport
}

/**
 * Score popup controller. Construct with the HUD root (the 9:16 viewport
 * container). Call `spawnKick` / `spawnNearMiss` from event handlers; call
 * `update(dt)` each render frame.
 */
export class ScorePopups {
  private readonly slots: PopupSlot[] = [];
  private nextIdx = 0;

  constructor(root: HTMLDivElement) {
    for (let i = 0; i < PP.poolSize; i++) {
      const el = document.createElement("div");
      Object.assign(el.style, {
        position: "absolute",
        font: PP.font,
        color: PP.colorKick,
        textShadow: "0 2px 4px #000",
        pointerEvents: "none",
        transform: "translate(-50%, -50%)",
        opacity: "0",
        whiteSpace: "nowrap",
        willChange: "transform, opacity",
      } as Partial<CSSStyleDeclaration>);
      el.style.display = "none";
      root.appendChild(el);
      this.slots.push({ el, active: false, elapsed: 0, startX: 0, startY: 0 });
    }
  }


  /**
   * Spawn a kick score popup.
   * @param kind     "car" | "truck" — determines text + color.
   * @param screenX  pixel x within the 9:16 viewport (0 = left edge).
   * @param screenY  pixel y within the 9:16 viewport (0 = top edge).
   */
  spawnKick(kind: "car" | "truck", screenX: number, screenY: number): void {
    const text = kind === "truck" ? PP.kickTruckText : PP.kickCarText;
    this.spawn(text, PP.colorKick, screenX, screenY);
  }

  /**
   * Spawn the DOUBLE KICK banner (both legs, two vehicles, one cooldown).
   * Same pooled rise-and-fade as every other popup, in the hotter colour.
   */
  spawnDoubleKick(screenX: number, screenY: number): void {
    this.spawn(PP.doubleKickText, PP.colorDoubleKick, screenX, screenY);
  }

  /** Spawn a near-miss popup (centred upper area). */
  spawnNearMiss(screenX: number, screenY: number): void {
    this.spawn(PP.nearMissText, PP.colorNearMiss, screenX, screenY);
  }

  /** Advance all active popups by `dt` seconds. */
  update(dt: number): void {
    for (let i = 0; i < this.slots.length; i++) {
      const s = this.slots[i];
      if (!s.active) continue;
      s.elapsed += dt;
      const t = clamp01(s.elapsed / PP.duration);
      if (t >= 1) {
        s.active = false;
        s.el.style.display = "none";
        s.el.style.opacity = "0";
        continue;
      }
      // Rise + fade: opacity follows 1→0 over the duration (with a quick fade-in).
      const fadeIn = clamp01(t / 0.15);
      const fadeOut = 1 - clamp01((t - 0.3) / 0.7);
      const opacity = fadeIn * fadeOut;
      const rise = t * PP.risePx;
      s.el.style.transform = `translate(-50%, calc(-50% - ${rise}px)) scale(${1 + t * 0.2})`;
      s.el.style.opacity = opacity.toFixed(3);
    }
  }

  reset(): void {
    for (let i = 0; i < this.slots.length; i++) {
      const s = this.slots[i];
      s.active = false;
      s.el.style.display = "none";
      s.el.style.opacity = "0";
    }
  }

  dispose(): void {
    for (const s of this.slots) {
      s.el.remove();
    }
    this.slots.length = 0;
  }

  // -----------------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------------

  private spawn(text: string, color: string, x: number, y: number): void {
    const s = this.slots[this.nextIdx];
    this.nextIdx = (this.nextIdx + 1) % this.slots.length;
    s.active = true;
    s.elapsed = 0;
    s.startX = x;
    s.startY = y;
    s.el.textContent = text;
    s.el.style.color = color;
    s.el.style.left = `${x}px`;
    s.el.style.top = `${y}px`;
    s.el.style.display = "block";
    s.el.style.opacity = "0";
    s.el.style.transform = "translate(-50%, -50%)";
  }
}
